/**
 * Hand-to-hand match transition (T14). Scores a concluded hand (Classical
 * only — `exhausted` hands never score, see `matchScoringContext.ts`),
 * applies the traditional "dealer plays for double" point payments (see
 * `matchPayments.ts`) to the running per-seat scores, decides whether the
 * dealer repeats or rotates (and whether the round flips or the match ends —
 * see `MatchState.ts`'s file header for the rule this implements), and
 * either builds the next hand or signals match completion. Pure — no
 * canvas/DOM/timers, matching every other `match/`/`game/` module.
 */

import { createHand } from '../game/GameState.js';
import type { HandResult, MahjongState } from '../game/stateCore.js';
import { SEAT_COUNT, type Seat } from '../game/types.js';
import type { Ruleset } from '../game/ruleset/types.js';
import { applyHandPayments } from './matchPayments.js';
import { scoreConcludedHand } from './matchScoringContext.js';
import { nextSeat, type MatchState, type Round } from './MatchState.js';

export interface AdvanceMatchResult {
  readonly matchState: MatchState;
  /** `null` exactly when the match just finished. */
  readonly nextHand: MahjongState | null;
}

interface DealerTransition {
  readonly finished: boolean;
  readonly dealer: Seat;
  readonly dealerSeatsSeen: number;
  readonly round: Round;
}

/** Dealer-repeat-vs-rotate/round-flip/match-end decision — see `MatchState.ts`'s file header for the rule. */
function decideDealerTransition(matchState: MatchState, result: HandResult): DealerTransition {
  const dealerWon = result.kind !== 'exhausted' && result.winner === matchState.dealer;
  if (dealerWon || result.kind === 'exhausted') {
    return {
      finished: false,
      dealer: matchState.dealer,
      dealerSeatsSeen: matchState.dealerSeatsSeen,
      round: matchState.round,
    };
  }
  const dealer = nextSeat(matchState.dealer);
  const dealerSeatsSeen = matchState.dealerSeatsSeen + 1;
  if (dealerSeatsSeen <= SEAT_COUNT) {
    return { finished: false, dealer, dealerSeatsSeen, round: matchState.round };
  }
  if (matchState.round === 'east') {
    return { finished: false, dealer, dealerSeatsSeen: 1, round: 'south' };
  }
  return { finished: true, dealer, dealerSeatsSeen, round: matchState.round };
}

/**
 * Transitions from one finished hand to the next. `concludedState` must be a
 * hand-end state (`phase === 'hand-end'`, `result !== null`). `nextSeed` is
 * ignored when the match just finished.
 */
export function advanceMatch(
  matchState: MatchState,
  concludedState: MahjongState,
  ruleset: Ruleset,
  nextSeed: number,
): AdvanceMatchResult {
  const result = concludedState.result;
  if (result === null) throw new Error('advanceMatch requires a concluded hand (hand-end phase)');

  const handScore = scoreConcludedHand(matchState, concludedState, result, ruleset);
  const scores = applyHandPayments(matchState.scores, matchState.dealer, result, handScore);
  const transition = decideDealerTransition(matchState, result);

  if (transition.finished) {
    return { matchState: { ...matchState, scores, finished: true }, nextHand: null };
  }

  const nextMatchState: MatchState = {
    round: transition.round,
    dealer: transition.dealer,
    dealerSeatsSeen: transition.dealerSeatsSeen,
    scores,
    finished: false,
  };
  return { matchState: nextMatchState, nextHand: createHand(nextSeed, ruleset, nextMatchState.dealer) };
}
